import { useUser } from '~/lib/hooks'
import { processClientSideLogoForUpload } from '../helpers/logos'
import { stringExistsAndHasChars } from '../helpers/string'

/**
 *
 *
 * @param {import('@nuxtjs/axios').NuxtAxiosInstance} api
 */
export default api => ({
  async listPaginatedSavedLogos(
    cursor = undefined,
    logos = [],
    limit = 50,
    query = undefined
  ) {
    const { getUser } = useUser(api)
    const user = await getUser()
    const wordmarkQuery = stringExistsAndHasChars(query) ? query : undefined
    const params = {
      limit,
      order: 'desc',
      cursor: cursor || undefined,
      query: wordmarkQuery,
    }
    const { data } = await api.get(`/api/v2/logos/saved/${user.id}`, {
      params,
    })

    const saved_logos = data.data
    logos.push(...saved_logos)
    const nextCursor = data.next_cursor

    return { data: logos, next_cursor: nextCursor, has_more: !!nextCursor }
  },

  async listAllSavedLogos() {
    const { getUser } = useUser(api)
    const user = await getUser()
    let next
    const logos = []
    do {
      const { data } = await api.get(`/api/v2/logos/saved/${user.id}`, {
        params: {
          order: 'desc',
          cursor: next || undefined,
        },
      })

      const saved_logos = data.data
      logos.push(...saved_logos)
      next = data.next_cursor
    } while (next)
    return {
      data: logos,
    }
  },

  /**
   * Saves the logo, returning it.
   *
   * @param {string} id An ID of the logo to save
   * @returns {Logo}
   */
  async addSavedLogo(id) {
    const { getUser } = useUser(api)
    const user = await getUser()

    const { data } = await api.post(`/api/v2/logos/saved/user/${user.id}/${id}`)

    return data
  },

  /**
   * Favorites the logo, returning it
   * @param {*} logo logo object to favorite
   * @param {*} eid enter
   * @returns
   */
  async favoriteClientSideLogo(logo, eid) {
    const { getUser } = useUser(api)
    const user = await getUser()
    const formdata = processClientSideLogoForUpload(
      logo.id,
      logo,
      user,
      eid,
      true
    )
    const { data } = await api.post(
      `/api/v2/logos/saved/favorite/${user.id}/${logo.id}`,
      formdata,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=${formdata._boundary}`,
        },
      }
    )

    return data
  },

  /**
   * Unfavorites the logo
   * @param {*} logo logo object to unfavorite
   * @returns
   */
  async unfavoriteClientSideLogo(logo) {
    const { getUser } = useUser(api)
    const user = await getUser()

    const { data } = await api.delete(
      `/api/v2/logos/favourites/${user.id}/${logo.id}`,
      {}
    )

    return data
  },

  /**
   * Saves the logo, returning it.
   *
   * @param {string} id An ID of the logo to save
   * @returns {Logo}
   */
  async addSavedClientSideLogo(logo, eid) {
    const { getUser } = useUser(api)
    const user = await getUser()
    const formdata = processClientSideLogoForUpload(logo.id, logo, user, eid)
    const { data } = await api.post(
      `/api/v2/logos/saved/userlogo/${user.id}/${logo.id}`,
      formdata,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=${formdata._boundary}`,
        },
      }
    )

    return data
  },

  /**
   * Saves the logo, returning it.
   *
   * @param {string} id An ID of the logo to save
   * @returns {Logo}
   */
  async shareClientSideLogo(logo, eid) {
    const { getUser } = useUser(api)
    const user = await getUser()
    const formdata = processClientSideLogoForUpload(logo.id, logo, user, eid)
    const { data } = await api.post(`/api/v2/logos/saved/share`, formdata, {
      headers: {
        'Content-Type': `multipart/form-data; boundary=${formdata._boundary}`,
      },
    })

    return data
  },

  /**
   * Patches a logo in place.
   *
   * @param {string} id The id of the logo to update
   */
  async updateSavedLogo(id, payload) {
    const assets = {
      ...payload.assets,
    }
    const icon = assets?.icons?.[0] || assets.icons.icon
    if (icon) {
      assets.icons = [icon]
    } else {
      assets.icons = []
    }
    const palette = {
      ...assets.palette,
    }
    if (!palette.id) {
      palette.id = 'CUSTOM'
    }
    const body = {
      ...payload,
      assets: {
        ...assets,
        palette,
      },
      svg: undefined,
      svg_code: undefined,
    }
    const { data } = await api.patch(`/api/v2/logos/saved/${id}`, body)

    return data
  },

  async updateClientSideSavedLogo(id, logo, eid) {
    const { getUser } = useUser(api)
    const user = await getUser()
    const logo_to_update = {
      ...logo,
      id,
    }
    const formdata = processClientSideLogoForUpload(
      id,
      logo_to_update,
      user,
      eid
    )
    try {
      const { data } = await api.post(
        `/api/v2/logos/saved/userlogo/${user.id}/${id}`,
        formdata,
        {
          headers: {
            'Content-Type': `multipart/form-data; boundary=${formdata._boundary}`,
          },
        }
      )

      return data
    } catch (error) {
      console.log('error while saving logo', error)
    }
  },

  /**
   * Immediately unsaves the logo from the users account.
   *
   * @param {string} id THe ID of the logo to archive
   */
  async removeSavedLogoFromAccount(id) {
    const { data } = await api.post(`/api/v2/logos/saved/${id}/archive`)

    return data
  },

  /**
   * Returns a single logo by ID.  Retrieved logos can be saved or unsaved.
   *
   * @param {string} id The LOGO id to retrieve
   */
  async retrieveLogo(id) {
    const { data } = await api.get(`/api/v2/logos/${id}`)

    return data
  },
})
